import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Loader2, ShieldCheck, LogOut } from "lucide-react";
import { pinStatus, verifyPin } from "@/lib/api";
import { getDeviceId, guessDeviceLabel } from "@/lib/device";
import { useAuth } from "@/context/AuthContext";

const IDLE_LOCK_MS = 10 * 60 * 1000; // 10 minutes of inactivity → re-lock

/**
 * Full-screen PIN gate. Renders itself when:
 *   (a) the user has a PIN set AND
 *   (b) they haven't provided a valid pin_token this browser session AND
 *   (c) either it's a fresh page load OR 10 minutes of inactivity passed.
 *
 * On success we stash a session-scoped flag in sessionStorage (so refresh
 * inside a tab is smooth) and add global mousemove/keydown listeners for the
 * idle timeout.
 */
export const PinGate = ({ children }) => {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);   // {enabled, locked_until, failed_attempts} | null
  const [locked, setLocked] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);

  // Check current PIN status when a user logs in. Pass the persistent device_id
  // so the server can tell us to skip the prompt on a trusted laptop.
  useEffect(() => {
    if (!user) { setStatus(null); setLocked(false); return; }
    const deviceId = getDeviceId();
    pinStatus(deviceId).then((s) => {
      setStatus(s);
      if (s.enabled && !s.bypass) {
        // Consider already-unlocked if we did so this session
        const ok = sessionStorage.getItem("lectura_pin_ok") === "1";
        setLocked(!ok);
      } else {
        // Either no PIN set OR this browser is on the trusted-devices list
        setLocked(false);
      }
    }).catch(() => setStatus({ enabled: false }));
  }, [user]);

  // Idle re-lock — only wire the listener when a PIN is enabled + we're currently unlocked
  const scheduleIdle = useCallback(() => {
    if (!status?.enabled || locked) return;
    // Store last-active timestamp; a tick every 30s checks the delta
    let lastActive = Date.now();
    const bump = () => { lastActive = Date.now(); };
    const evs = ["mousemove", "keydown", "click", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => {
      if (Date.now() - lastActive > IDLE_LOCK_MS) {
        sessionStorage.removeItem("lectura_pin_ok");
        setLocked(true);
      }
    }, 30_000);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(iv);
    };
  }, [status?.enabled, locked]);
  useEffect(() => scheduleIdle(), [scheduleIdle]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin || checking) return;
    setChecking(true);
    setError("");
    try {
      await verifyPin(pin, {
        device_id: getDeviceId(),
        trust_device: trustDevice,
        device_label: guessDeviceLabel(),
      });
      sessionStorage.setItem("lectura_pin_ok", "1");
      setLocked(false);
      setPin("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Wrong PIN — try again.");
      setPin("");
    } finally {
      setChecking(false);
    }
  };

  const signOut = async () => {
    sessionStorage.removeItem("lectura_pin_ok");
    if (logout) await logout();
    window.location.href = "/login";
  };

  const showGate = !!(user && status?.enabled && locked);
  return (
    <>
      {children}
      <AnimatePresence>
        {showGate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] grid place-items-center bg-primary text-primary-foreground p-6"
            data-testid="pin-gate"
          >
            <div className="w-full max-w-sm text-center">
              <span className="grid place-items-center h-14 w-14 rounded-2xl bg-primary-foreground/15 mx-auto">
                <Lock className="h-7 w-7" />
              </span>
              <h1 className="mt-5 font-serif text-3xl font-bold">Enter your PIN</h1>
              <p className="mt-2 text-sm opacity-80">Lectura is locked. Enter your 4-6 digit PIN to unlock this device.</p>
              <form onSubmit={submit} className="mt-6">
                <input
                  data-testid="pin-input"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoFocus
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full text-center tracking-[0.5em] font-mono text-3xl rounded-2xl bg-primary-foreground/15 border-2 border-transparent focus:border-accent focus:ring-0 outline-none py-4 placeholder:opacity-40"
                  placeholder="••••"
                />
                {error && (
                  <p data-testid="pin-error" className="mt-3 text-sm text-red-200 bg-red-900/30 rounded-lg px-3 py-2">{error}</p>
                )}
                <label className="mt-4 flex items-center justify-center gap-2 text-xs opacity-80 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="pin-trust-device"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="h-4 w-4 rounded border-primary-foreground/40 accent-accent cursor-pointer"
                  />
                  Trust this device for 30 days
                </label>
                <button
                  type="submit"
                  data-testid="pin-submit"
                  disabled={pin.length < 4 || checking}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition"
                >
                  {checking ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking…</> : <><ShieldCheck className="h-4 w-4" /> Unlock</>}
                </button>
              </form>
              <button
                onClick={signOut}
                data-testid="pin-signout"
                className="mt-4 inline-flex items-center gap-1.5 text-xs opacity-70 hover:opacity-100 transition"
              >
                <LogOut className="h-3.5 w-3.5" /> Sign out instead
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
